'use strict'

import { uuid, merge, isObject } from '@akerolabs/akero-vue/src/utils/helpers'
import config from './config'

/**
 * @param {Mixed} str
 * @param {String} message
 * @description Throws an error is the str variable is not of type
 * "string".
 */
function checkString(str, message) {
  if (typeof str !== 'string') throw new TypeError(message)
}

/**
 * @param {Mixed} obj
 * @param {String} message
 * @description Throws an error is the obj variable is not of type
 * "Object".
 */
function checkObject(obj, message) {
  if (!isObject(obj)) throw new TypeError(message)
}

/**
 * @param {String} title
 * @param {Array} blockContent
 * @param {Object} opts
 * @returns {Object}
 * @description Returns an empty template object
 */
export function newTemplate(title, blockContent = [], options = {}) {
  checkString(title, 'title must be a string')
  checkObject(options, 'options must be an object')

  const settings = merge(config.defaultTemplateStyles, options, true)
  const opts = merge(config.defaultTemplateOpts, {}, true)
  let blocks = []

  if (Array.isArray(blockContent)) {
    blocks = blockContent
  }

  return {
    version: 2,
    settings,
    opts,
    blocks,
    title,
  }
}

/**
 * @param {String} type
 * @param {Array} blockContent
 * @param {Object} opts
 * @returns {Object}
 * @description Returns an empty block object
 */
export function newBlock(type, blockContent = [], opts = {}) {
  checkString(type, 'type must be a string')
  checkObject(opts, 'opts must be an object')

  const settings = merge(config.defaultBlockOptions, opts, true)
  let content = []

  if (Array.isArray(blockContent)) {
    content = blockContent
  }

  return {
    uuid: uuid(),
    settings,
    content,
    type,
  }
}

/**
 * @param {String} content
 * @param {Object} styles
 * @returns {Object}
 * @description Returns a new title object
 */
export function newTitle(content = config.defaultTitle, styles = {}) {
  checkString(content, 'content must be a string')
  checkObject(styles, 'styles must be an object')

  const style = merge(config.defaultTitleOptions, styles, true)

  return {
    type: 'title',
    uuid: uuid(),
    content,
    style,
  }
}

export function newText(content = config.defaultContent, styles = {}) {
  checkString(content, 'content must be a string')
  checkObject(styles, 'styles must be an object')

  const style = merge(config.defaultContentOptions, styles, true)

  return {
    type: 'text',
    uuid: uuid(),
    content,
    style,
  }
}

/**
 * @param {String} src
 * @param {Object} styles
 * @returns {Object}
 * @description Returns a new image object
 */
export function newImage(src = config.defaultImage, styles = {}) {
  checkString(src, 'src must be a string')
  checkObject(styles, 'styles must be an object')

  const style = merge(config.defaultImageOptions, styles, true)

  return {
    type: 'image',
    uuid: uuid(),
    src,
    style,
  }
}

/**
 * @param {Object} styles
 * @returns {Object}
 * @description Returns a new divider object
 */
export function newDivider(styles = {}) {
  checkObject(styles, 'styles must be an object')

  const style = merge(config.defaultDividerOptions, styles, true)

  return {
    type: 'divider',
    uuid: uuid(),
    style,
  }
}

/**
 * @param {Object} styles
 * @returns {Object}
 * @description Returns a new button object
 */
export function newButton(
  value = config.defaultButtonText,
  href = config.defaultButtonLink,
  target = config.defaultButtonTarget,
  styles = {},
) {
  checkString(value, 'value must be a string')
  checkString(href, 'href must be a string')
  checkString(target, 'target must be a string')
  checkObject(styles, 'styles must be an object')

  const style = merge(config.defaultButtonOptions, styles, true)

  return {
    uuid: uuid(),
    type: 'button',
    value,
    href,
    target,
    style,
  }
}

export function newHTML(content = config.defaultHTMLContent, styles = {}) {
  checkString(content, 'content must be a string')
  checkObject(styles, 'styles must be an object')

  const style = merge(config.defaultHTMLOptions, styles, true)

  return {
    type: 'html',
    uuid: uuid(),
    content,
    style,
  }
}

export function newSignature(content = '', styles = {}) {
  checkString(content, 'content must be a string')
  checkObject(styles, 'styles must be an object')
  const style = merge({}, styles, true)
  return {
    type: 'signature',
    uuid: uuid(),
    content,
    style,
  }
}
